import { LoaderCircle, Square } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AgentTerminalDisplay } from '@/components/desktop-panel-primitives';
import {
  type BackgroundTerminalCompletion,
  backgroundOutputCompletion,
  terminalExitLabel,
} from '@/lib/conversations/message-part/terminal';
import { formatDuration } from '@/lib/conversations/message-part/turn-steps';
import { errorMessage } from '@/lib/error-message';
import { readNativeAgentBackgroundOutput } from '@/lib/native-desktop';

const BACKGROUND_OUTPUT_REFRESH_MS = 1_000;

/** How long a command ran: in milliseconds under a second, as a turn's steps say it above. */
function commandDuration(milliseconds: number): string {
  return milliseconds < 1_000 ? `${Math.round(milliseconds)} ms` : formatDuration(milliseconds);
}

export function AgentTerminalPane({
  terminal,
  visible,
  onKill,
  onStopped,
}: {
  terminal: AgentTerminalDisplay;
  visible: boolean;
  onKill: (terminalId: string, outputFile?: string) => Promise<void>;
  onStopped?: (terminalId: string, completion?: BackgroundTerminalCompletion) => void;
}) {
  const outputRef = useRef<HTMLPreElement>(null);
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState<string | null>(null);
  const [backgroundOutput, setBackgroundOutput] = useState<string | null>(null);
  const [backgroundStopped, setBackgroundStopped] = useState(false);

  useEffect(() => {
    const outputFile = terminal.outputFile;
    if (!visible || outputFile === undefined) {
      setBackgroundOutput(null);
      return;
    }

    let cancelled = false;
    const refresh = () => {
      void readNativeAgentBackgroundOutput(outputFile)
        .then((output) => {
          if (!cancelled) setBackgroundOutput(output);
        })
        .catch(() => {
          // The task creates its output file asynchronously. Keep polling so a
          // transient ENOENT does not replace the terminal with the tool's
          // "Command running in background" acknowledgement.
        });
    };
    refresh();
    const interval = window.setInterval(refresh, BACKGROUND_OUTPUT_REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [terminal.outputFile, visible]);

  const displayedOutput =
    terminal.outputFile === undefined
      ? terminal.output
      : (backgroundOutput ?? 'Waiting for terminal output…');
  const backgroundTask = terminal.outputFile !== undefined;
  const backgroundCompletion = useMemo(
    () => (backgroundOutput === null ? null : backgroundOutputCompletion(backgroundOutput)),
    [backgroundOutput],
  );
  const stopped =
    backgroundStopped ||
    backgroundCompletion !== null ||
    terminal.backgroundCompleted === true ||
    terminal.signal !== null;
  // The agent runs its foreground commands itself: they stop with its turn. A background task
  // outlives the turn, and stops here.
  const canStop = backgroundTask && !stopped;
  const completionExitCode = backgroundCompletion?.exitCode ?? terminal.exitCode;
  const completionSignal = backgroundCompletion?.signal ?? terminal.signal;

  useEffect(() => {
    if (backgroundCompletion !== null && terminal.backgroundCompleted !== true) {
      onStopped?.(terminal.id, backgroundCompletion);
    }
  }, [backgroundCompletion, onStopped, terminal.backgroundCompleted, terminal.id]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(displayedOutput): the output follows its end each time it grows.
  useEffect(() => {
    if (!visible) return;
    const output = outputRef.current;
    if (output !== null) output.scrollTop = output.scrollHeight;
  }, [displayedOutput, visible]);

  return (
    <div className={visible ? 'flex h-full min-h-0 flex-col bg-desktop-terminal' : 'hidden'}>
      <div className="flex shrink-0 items-center gap-3 border-white/10 border-b px-4 py-2 text-desktop-terminal-foreground text-xs">
        <code className="min-w-0 flex-1 truncate">{terminal.command}</code>
        <span className="shrink-0 text-desktop-terminal-foreground/60">
          {stopped
            ? backgroundStopped || completionSignal !== null
              ? 'Stopped'
              : terminalExitLabel(completionExitCode, null)
            : terminal.running || backgroundTask
              ? stopping
                ? 'Stopping…'
                : 'Running'
              : terminalExitLabel(terminal.exitCode, terminal.signal, terminal.toolState)}
          {terminal.durationMs !== undefined && !terminal.running && (
            <> · {commandDuration(terminal.durationMs)}</>
          )}
        </span>
        {canStop && (
          <button
            className="flex h-control-compact shrink-0 items-center gap-1.5 rounded-md border border-white/15 px-2 text-desktop-terminal-foreground/80 transition hover:bg-white/10 hover:text-desktop-terminal-foreground disabled:opacity-50"
            disabled={stopping}
            onClick={() => {
              setStopError(null);
              setStopping(true);
              const stop = onKill(terminal.id, terminal.outputFile);
              void stop
                .then(() => {
                  if (terminal.outputFile !== undefined) setBackgroundStopped(true);
                  onStopped?.(terminal.id);
                })
                .catch((error) => setStopError(errorMessage(error)))
                .finally(() => setStopping(false));
            }}
            type="button"
          >
            {stopping ? (
              <LoaderCircle className="size-3 animate-spin" />
            ) : (
              <Square className="size-3 fill-current" />
            )}
            Stop
          </button>
        )}
      </div>
      {terminal.cwd !== undefined && (
        <p
          className="shrink-0 truncate border-white/10 border-b px-4 py-1.5 font-mono text-desktop-terminal-foreground/60 text-xs"
          title={terminal.cwd}
        >
          {terminal.cwd}
        </p>
      )}
      {stopError !== null && (
        <p className="shrink-0 border-destructive/30 border-b bg-destructive/10 px-4 py-2 text-destructive text-xs">
          Could not stop terminal: {stopError}
        </p>
      )}
      <pre
        className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap p-4 font-mono text-desktop-terminal-foreground text-xs leading-5"
        ref={outputRef}
      >
        {displayedOutput.length > 0
          ? displayedOutput
          : canStop || terminal.running
            ? 'Waiting for output…'
            : 'No terminal output.'}
      </pre>
      {terminal.truncated && (
        <p className="shrink-0 border-white/10 border-t px-4 py-2 text-desktop-terminal-foreground/60 text-xs">
          Earlier output was truncated.
        </p>
      )}
    </div>
  );
}
